import { CancelToken } from 'axios';
import * as _ from 'lodash';
import { SyntheticEvent } from 'react';
import { InjectedIntlProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';

import { AccountLevelEnum, UserRole } from '@app/app.enum';
import { IHistoryState } from '@app/app.routes';
import { IInjectedAppModuleProps } from '@app/providers/app-module.provider';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { IErrorBody } from './shared.models';

const accountService: AccountService = Injector.get(AccountService);

const intlService: IntlService = Injector.get(IntlService);

// #region Sidebar models

export interface ISidebarComponentProps extends InjectedIntlProps, IInjectedAppModuleProps {
	open?: boolean;
	variant: 'permanent' | 'temporary';
	onToggle: () => void;
	onSidebarOpen?: () => void;
	version: string;
}

export interface ISidebarState {
	module: IModuleType;
	collapse: { [key in ModuleName]?: boolean };
}

export const initialSidebarState = (): ISidebarState => ({
	module: Module.Dashboard,
	collapse: {
		Reports: false,
	},
});

// #endregion

// #region Header models

export interface IBreadCrumb {
	title: string;
	url: string;
	state: any;
}

export interface IHeaderComponentProps extends RouteComponentProps<any, any, IHistoryState>, IInjectedAppModuleProps {
	onSidebarToggle?: () => void;
	onUserSignOut?: () => void;
	sidebarOpen?: boolean;
}

export interface IHeaderComponentState {
	isClosed: boolean;
	status: string;
}

export const initialHeaderState = (): IHeaderComponentState => ({
	isClosed: false,
	status: accountService.AccountStatus,
});

export interface IBaseEntity {
	id: string;
	isActive: boolean;
	createdDate: Date;
	createdBy: string;
	modifiedDate: Date;
	modifiedBy: string;
}
// #endregion

// #region Other models

// Material UI menu state object
export interface IMenuInjectedProps {
	open: (eventOrAnchorEl: SyntheticEvent<any> | HTMLElement) => void;
	close: () => void;
	onMouseLeave: (event: SyntheticEvent<any>) => void;
	toggle: (eventOrAnchorEl: SyntheticEvent<any> | HTMLElement) => void;
	setOpen: (open: boolean, eventOrAnchorEl: SyntheticEvent<any> | HTMLElement) => void;
	isOpen: boolean;
	anchorEl?: HTMLElement;
	popupId?: string;
	variant: 'popover' | 'popper';
}

// #endregion

//#region Location Props (Route Props)
export interface ILocationProps {
	hash?: string;
	pathname?: string;
	search?: string;
	state?: any;
}
//#endregion

type ModuleName =
	| 'Dashboard'
	| 'Apps'
	| 'Customers'
	| 'Accounts'
	| 'AccountDetails'
	| 'AccountAppDetails'
	| 'Shipments'
	| 'Carriers'
	| 'Carrier'
	| 'Carrier Accounts'
	| 'Settings'
	| 'Reports'
	| 'Billing'
	| 'Profile'
	| 'User'
	| 'SignOut'
	| 'ReportShipment'
	| 'ReportAccountStatementByDay'
	| 'ReportDailyFinance'
	| 'ReportMonthlyFinance'
	| 'ReportAccountStatement'
	| 'ShipmentManifestComponent';

export interface IModule {
	Dashboard: IModuleType;
	Customers: IModuleType;
	Accounts: IModuleType;
	CustomerAccounts: IModuleType;
	PartnerAccounts: IModuleType;
	AccountDetails: IModuleType;
	AccountAppDetails: IModuleType;
	Shipments: IModuleType;
	AdminShipments: IModuleType;
	ShipmentManifestComponent: IModuleType;
	Carriers: IModuleType;
	CarrierAccounts: IModuleType;
	Settings: IModuleType;
	Reports: IModuleType;
	Billing: IModuleType;
	Profile: IModuleType;
	User: IModuleType;
	SignOut: IModuleType;
	Apps: IModuleType;
}

export interface IModuleType {
	sortOrder: number;
	location: 'sidebar' | 'header' | 'hidden';
	level: AccountLevelEnum[];
	name: ModuleName;
	parentName?: ModuleName;
	toString: () => string;
	url?: string;
	redirect?: string;
	icon?: IIconProps;
	isParent?: boolean;
	children?: {
		[key: string]: IModuleType;
	};
	collapse?: boolean;
	accountIsClosedValues: boolean[];
	userRoles: UserRole[] | 'all';
}

export const Module: IModule = {
	// Sidebar
	Dashboard: {
		sortOrder: 0,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
		name: 'Dashboard',
		toString: () => intlService.GetFormattedMessage('app.component.dashboard'),
		url: '/dashboard',
		icon: { name: 'cold-storage-capability-modified', fontSize: 'large', style: { transform: 'translateY(-1px)' } },
		accountIsClosedValues: [],
		userRoles: 'all',
	},
	Apps: {
		sortOrder: 1,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer],
		name: 'Apps',
		toString: () => intlService.GetFormattedMessage('app.component.app'),
		url: '/apps',
		icon: { name: 'integrated-offerings', fontSize: 'large', style: { transform: 'translateY(1px)' } },
		accountIsClosedValues: [],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
		parentName: 'Customers',
	},
	Customers: {
		sortOrder: 2,
		location: 'sidebar',
		name: 'Customers',
		level: [AccountLevelEnum.Partner],
		toString: () => intlService.GetFormattedMessage('app.component.customer'),
		url: '/customers',
		icon: { name: 'people', fontSize: 'large' },
		accountIsClosedValues: [],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	Accounts: {
		sortOrder: 2,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Admin],
		name: 'Accounts',
		toString: () => intlService.GetFormattedMessage('app.component.accounts'),
		url: '/accounts',
		redirect: '/accounts/customer',
		icon: { name: 'people', fontSize: 'large' },
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	CustomerAccounts: {
		sortOrder: 0,
		location: 'hidden',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Admin],
		name: 'Accounts',
		toString: () => intlService.GetFormattedMessage('app.component.accounts'),
		url: '/accounts/customer',
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	PartnerAccounts: {
		sortOrder: 1,
		location: 'hidden',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Admin],
		name: 'Accounts',
		toString: () => intlService.GetFormattedMessage('app.component.accounts'),
		url: '/accounts/partner',
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	AccountDetails: {
		sortOrder: 2,
		location: 'hidden',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
		name: 'AccountDetails',
		toString: () => intlService.GetFormattedMessage('app.component.account-detatils'),
		url: '/customer/:accounId',
		icon: { name: 'people', fontSize: 'large' },
		accountIsClosedValues: [],
		userRoles: 'all',
		parentName: 'Customers',
	},
	AccountAppDetails: {
		sortOrder: 2,
		location: 'hidden',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
		name: 'AccountAppDetails',
		toString: () => intlService.GetFormattedMessage('app.component.account-app-detatils'),
		url: '/customer/account/:appId',
		icon: { name: 'people', fontSize: 'large' },
		accountIsClosedValues: [],
		userRoles: 'all',
		parentName: 'Customers',
	},
	Shipments: {
		sortOrder: 3,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer],
		name: 'Shipments',
		toString: () => intlService.GetFormattedMessage('app.component.shipment'),
		url: '/shipment',
		icon: { name: 'boxes-stacked-together-modified', fontSize: 'large', style: { transform: 'translateY(-1px)' } },
		accountIsClosedValues: [],
		userRoles: [UserRole.User, UserRole.AccountAdmin],
	},
	Carriers: {
		sortOrder: 4,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
		name: 'Carriers',
		toString: () => intlService.GetFormattedMessage('app.component.carriers'),
		url: '/carriers',
		icon: { name: 'truck-side', fontSize: 'large', style: { transform: 'translateY(-2px)' } },
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
		children: {
			Carrier: {
				sortOrder: 0,
				location: 'sidebar',
				level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
				name: 'Carrier',
				parentName: 'Carriers',
				toString: () => intlService.GetFormattedMessage('app.component.carrier'),
				url: '/carrier',
				accountIsClosedValues: [],
				userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
			},
		},
	},
	CarrierAccounts: {
		sortOrder: 5,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer],
		name: 'Carrier Accounts',
		toString: () => intlService.GetFormattedMessage('app.component.carrier-accounts'),
		url: '/carrier-accounts',
		icon: { name: 'truck-side', fontSize: 'large', style: { transform: 'translateY(-2px)' } },
		accountIsClosedValues: [],
		userRoles: [UserRole.User, UserRole.AccountAdmin],
	},
	AdminShipments: {
		sortOrder: 5,
		location: 'sidebar',
		level: [AccountLevelEnum.Admin],
		name: 'Shipments',
		toString: () => intlService.GetFormattedMessage('app.component.shipment'),
		url: '/shipments',
		icon: { name: 'boxes-stacked-together-modified', fontSize: 'large', style: { transform: 'translateY(-1px)' } },
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	ShipmentManifestComponent: {
		sortOrder: 5,
		location: 'sidebar',
		level: [AccountLevelEnum.Admin],
		name: 'ShipmentManifestComponent',
		toString: () => intlService.GetFormattedMessage('app.component.shipment-manifests'),
		url: '/flagged-manifests',
		icon: { name: 'container-down', fontSize: 'large' },
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	Settings: {
		sortOrder: 6,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Admin],
		name: 'Settings',
		toString: () => intlService.GetFormattedMessage('app.component.settings'),
		url: '/settings',
		icon: { name: 'cog', fontSize: 'large' },
		accountIsClosedValues: [],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	Reports: {
		sortOrder: 7,
		location: 'sidebar',
		level: [AccountLevelEnum.Customer],
		name: 'Reports',
		toString: () => intlService.GetFormattedMessage('app.component.report'),
		icon: { name: 'data', fontSize: 'large' },
		isParent: true,
		accountIsClosedValues: [],
		userRoles: 'all',
		children: {
			ReportAccountStatement: {
				sortOrder: 0,
				location: 'sidebar',
				level: [AccountLevelEnum.Customer],
				name: 'ReportAccountStatement',
				parentName: 'Reports',
				toString: () => intlService.GetFormattedMessage('app.component.report-account-statement'),
				url: '/report/account-statement',
				accountIsClosedValues: [],
				userRoles: [UserRole.User, UserRole.AccountAdmin],
			},
			ReportAccountStatementByDay: {
				sortOrder: 1,
				location: 'sidebar',
				level: [AccountLevelEnum.Customer],
				name: 'ReportAccountStatementByDay',
				parentName: 'Reports',
				toString: () => intlService.GetFormattedMessage('app.component.report-account-statement-by-day'),
				url: '/report/account-statement-by-day',
				accountIsClosedValues: [],
				userRoles: [UserRole.User, UserRole.AccountAdmin],
			},
			ReportDailyFinance: {
				sortOrder: 2,
				location: 'sidebar',
				level: [AccountLevelEnum.Customer],
				name: 'ReportDailyFinance',
				parentName: 'Reports',
				toString: () => intlService.GetFormattedMessage('app.component.report-daily-finance'),
				url: '/report/daily-finance',
				accountIsClosedValues: [],
				userRoles: [UserRole.User, UserRole.AccountAdmin],
			},
			ReportMonthlyFinance: {
				sortOrder: 2,
				location: 'sidebar',
				level: [AccountLevelEnum.Customer],
				name: 'ReportMonthlyFinance',
				parentName: 'Reports',
				toString: () => intlService.GetFormattedMessage('app.component.report-monthly-finance'),
				url: '/report/monthly-finance',
				accountIsClosedValues: [],
				userRoles: [],
			},
		},
	},
	

	// Header
	Billing: {
		sortOrder: 0,
		location: 'header',
		level: [AccountLevelEnum.Customer],
		name: 'Billing',
		toString: () => intlService.GetFormattedMessage('app.component.billing'),
		url: '/billing/postage',
		accountIsClosedValues: [false],
		userRoles: [UserRole.User, UserRole.AccountAdmin],
	},
	Profile: {
		sortOrder: 1,
		location: 'header',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
		name: 'Profile',
		toString: () => intlService.GetFormattedMessage('app.component.account'),
		url: '/account',
		accountIsClosedValues: [false],
		userRoles: 'all',
	},
	User: {
		sortOrder: 3,
		location: 'header',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Admin],
		name: 'User',
		toString: () => intlService.GetFormattedMessage('app.component.users'),
		url: '/account/user',
		accountIsClosedValues: [false],
		userRoles: [UserRole.AccountAdmin, UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	SignOut: {
		sortOrder: 5,
		location: 'header',
		level: [AccountLevelEnum.Customer, AccountLevelEnum.Partner, AccountLevelEnum.Admin],
		name: 'SignOut',
		toString: () => intlService.GetFormattedMessage('app.component.signout'),
		accountIsClosedValues: [false, true],
		userRoles: 'all',
	},
};

export const getModuleArr = (): IModuleType[] => {
	let modules = _.values(Module);
	for (const m in modules) {
		if (modules.hasOwnProperty(m)) {
			const element = modules[m];
			if (element.children) {
				modules = modules.concat(_.values(element.children));
			}
		}
	}
	return modules.concat(_.values(Module.Reports.children));
};

export interface IPagedResult<TEntity> {
	results: TEntity[];
	nextPage: string;
	previousPage: string;
	pageSize: number;
	totalResults: number;
	totalPages: number;
}

export interface IErrorResponse {
	statusCode: number;
	timeStamp: Date;
	path: string;
	errors: IErrorBody[];
}

export interface IAxiosCancellation {
	axiosCancellationToken?: CancelToken;
	cancelActiveRequests?: () => Promise<void>;
	isRequestCancelled?: boolean;
}

export interface IIconProps extends React.HTMLAttributes<HTMLDivElement> {
	name: string;
	titleAccess?: string;
	className?: string;
	fontSize?: 'small' | 'medium' | 'large' | 'extra-large';
}

export interface IRibbonProps {
	text: string;
	position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
	color?: 'success' | 'info' | 'error' | 'warning';
	type?: 'squared' | 'rounded';
	classNames?: string;
}


// WEBPACK FOOTER //
// ./src/models/core.models.ts